// Admin console API surface — dashboard + users. docs/replan-v2.md (admin).
// Staff tool, English chrome.

import { API_BASE, api } from './api';

// ─── Dashboard ────────────────────────────────────────────────────────────

export interface Dashboard {
  users: { total: number; active: number; suspended: number; newThisWeek: number };
  families: number;
  schools: number;
  learners: number;
  subscriptions: Record<string, number>;
  mrrByCurrency: Record<string, number>;
  paidInvoices: number;
  lessonsCompleted: number;
  activeLast7d: number;
}

export async function getDashboard(signal?: AbortSignal): Promise<Dashboard> {
  const { dashboard } = await api<{ dashboard: Dashboard }>('/admin/dashboard', { signal });
  return dashboard;
}

// ─── Users ────────────────────────────────────────────────────────────────

export interface AdminUserRow {
  id: string;
  email: string | null;
  username: string | null;
  displayName: string | null;
  status: 'active' | 'suspended' | 'invited';
  locale: 'ar' | 'en';
  roles: string[];
  createdAt: string;
}

export interface AdminUserDetail extends AdminUserRow {
  country: string | null;
  phone: string | null;
  emailVerified: boolean;
  deletedAt: string | null;
  families: Array<{ id: string; role: string }>;
  schools: Array<{ id: string; name: string; role: string }>;
}

export interface UserListParams {
  q?: string;
  status?: 'all' | 'active' | 'suspended' | 'invited';
  kind?: 'all' | 'adult' | 'learner';
}

export async function listUsers(
  params: UserListParams,
  signal?: AbortSignal,
): Promise<AdminUserRow[]> {
  const sp = new URLSearchParams();
  if (params.q) sp.set('q', params.q);
  if (params.status && params.status !== 'all') sp.set('status', params.status);
  if (params.kind && params.kind !== 'all') sp.set('kind', params.kind);
  const qs = sp.toString();
  const { users } = await api<{ users: AdminUserRow[] }>(`/admin/users${qs ? `?${qs}` : ''}`, {
    signal,
  });
  return users;
}

export async function getUser(id: string, signal?: AbortSignal): Promise<AdminUserDetail> {
  const { user } = await api<{ user: AdminUserDetail }>(`/admin/users/${id}`, { signal });
  return user;
}

export async function suspendUser(id: string): Promise<void> {
  await api(`/admin/users/${id}/suspend`, { method: 'POST' });
}

export async function reactivateUser(id: string): Promise<void> {
  await api(`/admin/users/${id}/reactivate`, { method: 'POST' });
}

/**
 * Edit a user's identity + profile. `PATCH /admin/users/:id`.
 *
 * 🔴 Changing `email` or `username` REVOKES every session that user holds.
 * The access JWT carries the login identity and a generation counter; the API
 * bumps `users.auth_generation` when identity moves, which instantly kills
 * every outstanding access and refresh token. Editing a display name, phone,
 * country or locale does NOT — signing a customer out of every device because
 * staff fixed a typo would be worse than the typo.
 *
 * `sessionsRevoked` in the response says which happened, so the console can
 * tell the operator rather than leaving them to discover it via a support call.
 *
 * Only send the fields being changed: the body is `.strict()` server-side, so
 * an unknown key is a loud 400 rather than a silently-ignored edit.
 */
export interface UserEditPatch {
  email?: string | null;
  username?: string | null;
  displayName?: string | null;
  country?: string | null;
  phone?: string | null;
  locale?: 'ar' | 'en';
  status?: 'active' | 'suspended' | 'invited';
}

export interface UserEditResult {
  id: string;
  /** True when email/username moved and every token for the user was killed. */
  sessionsRevoked: boolean;
  /** The field names that actually changed. Empty on a no-op save. */
  changed: string[];
}

export async function updateUser(id: string, patch: UserEditPatch): Promise<UserEditResult> {
  return api<UserEditResult>(`/admin/users/${id}`, { method: 'PATCH', body: patch });
}

// Returns the new password once (admin hands it to the user).
export async function resetUserPassword(id: string): Promise<string> {
  const { password } = await api<{ password: string }>(`/admin/users/${id}/reset-password`, {
    method: 'POST',
    body: {},
  });
  return password;
}

// Starts a read-only impersonation session — swaps the admin's cookie for the
// target user's. The caller should then hard-navigate to the consumer app.
export async function impersonateUser(id: string): Promise<void> {
  await api(`/admin/users/${id}/impersonate`, { method: 'POST' });
}

// ─── Schools / Families drill-in ──────────────────────────────────────────

export interface OwnerSubscription {
  status: string;
  planNameKey: string;
  currency: string;
  interval: string;
  currentPeriodEnd: string | null;
}

export interface SchoolRow {
  id: string;
  name: string;
  slug: string;
  country: string;
  status: string;
  teacherCount: number;
  studentCount: number;
  classCount: number;
  subscriptionStatus: string | null;
  createdAt: string;
}

export interface SchoolDetail {
  id: string;
  name: string;
  slug: string;
  country: string;
  status: string;
  createdAt: string;
  subscription: OwnerSubscription | null;
  admins: Array<{ id: string; label: string }>;
  teachers: Array<{ id: string; label: string; status: string }>;
  classes: Array<{ id: string; name: string; grade: string; studentCount: number }>;
  studentCount: number;
}

export async function listSchools(signal?: AbortSignal): Promise<SchoolRow[]> {
  const { schools } = await api<{ schools: SchoolRow[] }>('/admin/schools', { signal });
  return schools;
}
export async function getSchool(id: string, signal?: AbortSignal): Promise<SchoolDetail> {
  const { school } = await api<{ school: SchoolDetail }>(`/admin/schools/${id}`, { signal });
  return school;
}

export interface FamilyRow {
  id: string;
  parentLabel: string;
  kidCount: number;
  status: string;
  subscriptionStatus: string | null;
  createdAt: string;
}

export interface FamilyDetail {
  id: string;
  status: string;
  createdAt: string;
  parent: { id: string; label: string; email: string | null } | null;
  kids: Array<{ id: string; label: string; username: string | null; status: string }>;
  subscription: OwnerSubscription | null;
}

export async function listFamilies(signal?: AbortSignal): Promise<FamilyRow[]> {
  const { families } = await api<{ families: FamilyRow[] }>('/admin/families', { signal });
  return families;
}
export async function getFamily(id: string, signal?: AbortSignal): Promise<FamilyDetail> {
  const { family } = await api<{ family: FamilyDetail }>(`/admin/families/${id}`, { signal });
  return family;
}

/**
 * Invoice history for one family, across EVERY subscription row it has held.
 *
 * 🔴 There is deliberately no `total` on this list. A family can hold invoices
 * in more than one currency and this system has no FX table anywhere, so a
 * single total would be fiction. The detail page groups by currency and uses
 * `sumMinor()` from `./money`, which throws rather than blend.
 *
 * Read-only by design: `invoices` is part of the append-only money ledger and
 * a refund is a SEPARATE row, never an edit to the invoice it reverses.
 */
export interface FamilyInvoice {
  id: string;
  subscriptionId: string;
  amountMinor: number;
  /** Before discount. Null on rows written before provenance columns existed. */
  subtotalMinor: number | null;
  discountMinor: number;
  currency: string;
  status: string;
  /** The plan THIS invoice billed for — may differ from the current plan. */
  planSlug: string | null;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
}

export async function listFamilyInvoices(
  id: string,
  signal?: AbortSignal,
): Promise<FamilyInvoice[]> {
  const { invoices } = await api<{ invoices: FamilyInvoice[] }>(`/admin/families/${id}/invoices`, {
    signal,
  });
  return invoices;
}

// ─── Kid progress (read-only view over a FROZEN engine) ───────────────────
//
// 🔴 The learning engine is frozen. These are GETs over a staff-only endpoint
// that does nothing but SELECT. There is no write counterpart and none may be
// added here — scoring, XP and streaks are owned by the engine alone.
//
// 🔴 Children's privacy (CLAUDE.md §4): the payload is counts, timestamps and
// identifiers. It carries no attempt payloads (the child's actual answers and
// audio), and the API audit-logs every read of a child's record.

export interface KidAttempt {
  id: string;
  activityId: string;
  activityKind: string | null;
  lessonTitleKey: string | null;
  score: number | null;
  /** Tri-state: null means this activity type is not graded at all. */
  correct: boolean | null;
  startedAt: string;
  endedAt: string | null;
}

export interface KidProgress {
  userId: string;
  label: string;
  username: string | null;
  status: string;
  lessonsCompleted: number;
  lessonsInProgress: number;
  xpTotal: number;
  streakCurrent: number;
  streakLongest: number;
  lastActiveDate: string | null;
  enrollments: number;
  badges: number;
  /** Mean over SCORED lessons. Null when nothing has been scored yet — which
   *  is a different fact from an average of zero, so it must not become 0. */
  averageScore: number | null;
  totalTimeSpentMs: number;
  lastLessonAt: string | null;
  recentAttempts: KidAttempt[];
}

export async function getFamilyProgress(
  familyId: string,
  signal?: AbortSignal,
): Promise<KidProgress[]> {
  const { progress } = await api<{ progress: { familyId: string; kids: KidProgress[] } }>(
    `/admin/progress/family/${familyId}`,
    { signal },
  );
  return progress.kids;
}

// ─── Promotions (coupons) ─────────────────────────────────────────────────
//
// One mechanism, three kinds (owner ruling 2026-08-16, M7a):
//   coupon        — the customer types a code
//   offer         — no code; auto-applies inside [startsAt, expiresAt]
//   plan_discount — auto-applies to a plan (+ optional interval, which is how
//                   a yearly-only discount is expressed)
//
// These mirror `couponRowDto` / `couponCreateInput` in
// packages/zod-schemas/src/admin.ts. 🔴 `code` is NULLABLE: the automatic
// kinds have none, and `coupons_code_uniq` is a partial index precisely so
// many code-less rows can coexist. Fall back to `name`, never to `''`.

export type PromotionKind = 'coupon' | 'offer' | 'plan_discount';

/** The only two states a promotion has. Archived is inert at checkout. */
export type CouponStatus = 'active' | 'archived';

export interface Coupon {
  id: string;
  kind: PromotionKind;
  code: string | null;
  name: string | null;
  discountPercent: number | null;
  discountAmountMinor: number | null;
  discountCurrency: string | null;
  maxRedemptions: number | null;
  maxRedemptionsPerOwner: number | null;
  redemptions: number;
  startsAt: string | null;
  expiresAt: string | null;
  appliesToPlanId: string | null;
  appliesToInterval: string | null;
  status: CouponStatus;
  createdAt: string;
}

export interface CreateCouponInput {
  kind: PromotionKind;
  code?: string | null;
  name?: string | null;
  discountPercent?: number | null;
  discountAmountMinor?: number | null;
  discountCurrency?: string | null;
  maxRedemptions?: number | null;
  maxRedemptionsPerOwner?: number | null;
  startsAt?: string | null;
  expiresAt?: string | null;
  appliesToPlanId?: string | null;
  appliesToInterval?: 'month' | 'year' | null;
}

export async function listCoupons(signal?: AbortSignal): Promise<Coupon[]> {
  const { coupons } = await api<{ coupons: Coupon[] }>('/admin/coupons', { signal });
  return coupons;
}
export async function createCoupon(input: CreateCouponInput): Promise<{ id: string }> {
  return api<{ id: string }>('/admin/coupons', { method: 'POST', body: input });
}
/**
 * Archive (or restore) a promotion. STATUS ONLY — there is no coupon edit.
 *
 * Editing a promotion that already has redemptions would rewrite history:
 * every `coupon_redemptions` row was priced by the terms the row held at the
 * time. Archiving is reversible and leaves that history intact, unlike DELETE
 * which cascades the redemptions away.
 */
export async function setCouponStatus(
  id: string,
  status: CouponStatus,
): Promise<{ id: string; status: CouponStatus }> {
  return api(`/admin/coupons/${id}`, { method: 'PATCH', body: { status } });
}

export async function deleteCoupon(id: string): Promise<void> {
  await api(`/admin/coupons/${id}`, { method: 'DELETE' });
}

// ─── Audit log ────────────────────────────────────────────────────────────

export interface AuditEntry {
  id: string;
  action: string;
  actorId: string | null;
  actorLabel: string | null;
  targetEntityType: string | null;
  targetEntityId: string | null;
  metadata: Record<string, unknown>;
  ip: string | null;
  createdAt: string;
}

export async function listAudit(
  params: { action?: string; actorId?: string },
  signal?: AbortSignal,
): Promise<AuditEntry[]> {
  const sp = new URLSearchParams();
  if (params.action) sp.set('action', params.action);
  if (params.actorId) sp.set('actorId', params.actorId);
  const qs = sp.toString();
  const { entries } = await api<{ entries: AuditEntry[] }>(`/admin/audit${qs ? `?${qs}` : ''}`, {
    signal,
  });
  return entries;
}

// ─── Feature flags ────────────────────────────────────────────────────────

export interface FeatureFlag {
  name: string;
  enabled: boolean;
  description: string;
}

export async function listFlags(signal?: AbortSignal): Promise<FeatureFlag[]> {
  const { flags } = await api<{ flags: FeatureFlag[] }>('/admin/settings/flags', { signal });
  return flags;
}
export async function upsertFlag(flag: FeatureFlag): Promise<void> {
  await api('/admin/settings/flags', { method: 'PUT', body: flag });
}
export async function deleteFlag(name: string): Promise<void> {
  await api(`/admin/settings/flags/${encodeURIComponent(name)}`, { method: 'DELETE' });
}

// ─── Announcements ────────────────────────────────────────────────────────

export interface AnnouncementInput {
  role: 'parent' | 'teacher' | 'school_admin' | 'kid' | 'student' | 'all';
  country?: string | null;
  title: string;
  body: string;
  link?: string | null;
}

export async function sendAnnouncement(input: AnnouncementInput): Promise<{ recipients: number }> {
  const { result } = await api<{ result: { recipients: number } }>('/admin/announcements', {
    method: 'POST',
    body: input,
  });
  return result;
}

// ─── Settings ─────────────────────────────────────────────────────────────

export interface Settings {
  brandName: string;
  supportEmail: string;
  aiStoryTone: string;
  aiImageStyle: string;
}

export async function getSettings(signal?: AbortSignal): Promise<Settings> {
  const { settings } = await api<{ settings: Settings }>('/admin/settings', { signal });
  return settings;
}
export async function updateSettings(patch: Partial<Settings>): Promise<void> {
  await api('/admin/settings', { method: 'PATCH', body: patch });
}

// ─── Reports (CSV download) ─────────────────────────────────────────────────

// Fetch a CSV report (with the session cookie) and trigger a browser download.
export async function downloadReport(kind: 'users' | 'revenue' | 'engagement'): Promise<void> {
  const resp = await fetch(`${API_BASE}/admin/reports/${kind}.csv`, { credentials: 'include' });
  if (!resp.ok) throw new Error(`Report failed (${resp.status})`);
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `fajrarabi-${kind}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
